/**
 * Единое правило порядка кластеров.
 *
 * Раньше порядок задавался в трёх местах по-разному: бэкенд отдавал кластеры
 * по частотности, стор сортировал корневые по `sort_order`, а сайдбар поверх
 * всего этого снова сортировал по частотности. В итоге перемещение кластера
 * сохранялось в базе, но на экране ничего не менялось.
 *
 * Правило одно: если пользователь задавал порядок руками — показываем его,
 * иначе показываем по частотности (как было раньше).
 */

export interface OrderableCluster {
  sort_order?: number | null;
  total_volume?: number | null;
  total_keywords_count?: number | null;
  keywords_count?: number | null;
  name?: string | null;
}

const orderOf = (cluster: OrderableCluster): number =>
  typeof cluster.sort_order === "number" ? cluster.sort_order : 0;

const volumeOf = (cluster: OrderableCluster): number =>
  cluster.total_volume || 0;

const countOf = (cluster: OrderableCluster): number =>
  cluster.total_keywords_count || cluster.keywords_count || 0;

/**
 * Задавал ли пользователь порядок в этой группе соседей.
 *
 * Если у всех соседей `sort_order` одинаковый (обычно нули у кластеров,
 * созданных вручную), то порядка нет и сортируем по частотности.
 */
export function hasManualOrder(clusters: OrderableCluster[]): boolean {
  if (clusters.length < 2) return false;

  const first = orderOf(clusters[0]);
  return clusters.some((cluster) => orderOf(cluster) !== first);
}

/** Запасное правило: частотность, затем количество ключей, затем имя. */
function compareByWeight(a: OrderableCluster, b: OrderableCluster): number {
  const byVolume = volumeOf(b) - volumeOf(a);
  if (byVolume !== 0) return byVolume;

  const byCount = countOf(b) - countOf(a);
  if (byCount !== 0) return byCount;

  return (a.name || "").localeCompare(b.name || "", "uk");
}

/**
 * Сортирует одну группу соседей. Исходный массив не меняется.
 */
export function sortClusters<T extends OrderableCluster>(clusters: T[]): T[] {
  if (clusters.length < 2) return [...clusters];

  if (!hasManualOrder(clusters)) {
    return [...clusters].sort(compareByWeight);
  }

  return [...clusters].sort((a, b) => {
    const byOrder = orderOf(a) - orderOf(b);
    return byOrder !== 0 ? byOrder : compareByWeight(a, b);
  });
}

/**
 * Сортирует дерево целиком: каждую группу соседей на каждом уровне.
 */
export function sortClusterTree<
  T extends OrderableCluster & { children?: T[] },
>(clusters: T[]): T[] {
  return sortClusters(clusters).map((cluster) =>
    cluster.children && cluster.children.length > 0
      ? { ...cluster, children: sortClusterTree(cluster.children) }
      : cluster,
  );
}
